import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ClipboardList,
  Printer,
  RefreshCw,
  Unlock,
} from 'lucide-react';
import { reservationService } from '../../../../services/reservationService';
import { printingService } from '../../../../services/printingService';
import { usePrinterFallback } from '../../../../hooks/usePrinterFallback';
import { StcReservation } from '../../../../types/reservation';
import { useAuth } from '../../../../contexts/AuthContext';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { BackButton } from '../../../common/BackButton';
import { WarehouseTable } from '../../../shared/table/components/WarehouseTable';
import { createColumnFactory } from '../../../shared/table/utils/tableUtils';
import type { ColumnDefinition, SummaryCalculation } from '../../../shared/table/types/table';

// Helper function to format date for display
const formatDateTime = (dateString: string): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString('no-NO', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Helper function to format numbers with thousand separator
const formatNumber = (num: number): string => {
  return num.toLocaleString('no-NO', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

const columnFactory = createColumnFactory<StcReservation>();

interface ReservationsAppProps {
  onBack?: () => void;
}

export const ReservationsApp: React.FC<ReservationsAppProps> = ({ onBack }) => {
  const { user } = useAuth();
  const { t } = useTranslation();

  // State management
  const [reservations, setReservations] = useState<StcReservation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedRowIds, setSelectedRowIds] = useState<(string | number)[]>([]);
  const { tryPrint: tryPrintLabel, modal: printerFallbackModal } = usePrinterFallback();

  // ResType-etiketter (1=plukk, 2=påfylling, 3=allokering, 50/51=flytt-låser, 99=manko)
  const resTypeLabel = useCallback((resType: number): string => {
    switch (resType) {
      case 1: return t('manager.modules.reservations.resTypes.picking', {}, 'Plukk');
      case 2: return t('manager.modules.reservations.resTypes.replenishment', {}, 'Påfylling');
      case 3: return t('manager.modules.reservations.resTypes.allocation', {}, 'Allokering');
      case 50: return t('manager.modules.reservations.resTypes.moveSource', {}, 'Flytt (kilde)');
      case 51: return t('manager.modules.reservations.resTypes.moveDestination', {}, 'Flytt (destinasjon)');
      case 99: return t('manager.modules.reservations.resTypes.shortage', {}, 'Manko');
      default: return String(resType);
    }
  }, [t]);

  // Load reservations
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const reservationsData = await reservationService.getActiveReservations();
      setReservations(reservationsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('manager.modules.reservations.loadError', {}, 'En ukjent feil oppstod'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  // Load on mount
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Column definitions
  const columns: ColumnDefinition<StcReservation>[] = useMemo(() => [
    columnFactory.createTextColumn('warehouseID', 'warehouseID', t('warehouse.general.warehouse', {}, 'Lager'), {
      width: 80,
      sortable: true,
      filterType: 'dropdown',
    }),
    columnFactory.createTextColumn('binCode', 'binCode', t('warehouse.general.bin', {}, 'Lokasjon'), {
      width: 120,
      sortable: true,
      filterType: 'both',
    }),
    columnFactory.createTextColumn('itemNo', 'itemNo', t('warehouse.general.itemNumber', {}, 'Varenummer'), {
      width: 130,
      sortable: true,
      filterType: 'both',
    }),
    columnFactory.createTextColumn('description1', 'description1', t('common.fields.description', {}, 'Beskrivelse'), {
      width: 200,
      sortable: true,
      filterType: 'text',
      formatter: (value) => value || '-',
    }),
    columnFactory.createNumberColumn('resQuantity', 'resQuantity', t('manager.modules.reservations.columns.resQuantity', {}, 'Ant. reservert'), {
      width: 120,
      sortable: true,
      alignment: 'right',
      formatter: (value, row) => {
        const qty = formatNumber(value as number);
        return row?.itemUnitCode ? `${qty} ${row.itemUnitCode}` : qty;
      },
    }),
    columnFactory.createDateColumn('resDate', 'resDate', t('manager.modules.reservations.columns.resDate', {}, 'Res. dato'), {
      width: 140,
      sortable: true,
      formatter: (value) => formatDateTime(value as string),
    }),
    columnFactory.createTextColumn('resType', 'resType', t('manager.modules.reservations.columns.resType', {}, 'Type'), {
      width: 110,
      sortable: true,
      filterType: 'dropdown',
      formatter: (value) => resTypeLabel(value as number),
    }),
    columnFactory.createTextColumn('orderNoERP', 'orderNoERP', t('manager.modules.reservations.columns.orderNoErp', {}, 'Ordre (ERP)'), {
      width: 110,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
    }),
    columnFactory.createTextColumn('orderLineERP', 'orderLineERP', t('manager.modules.reservations.columns.orderLineErp', {}, 'Linje (ERP)'), {
      width: 90,
      sortable: true,
      visible: true,
      formatter: (value) => value != null ? String(value) : '-',
    }),
    columnFactory.createTextColumn('orderName', 'orderName', t('manager.modules.reservations.columns.customer', {}, 'Kunde'), {
      width: 160,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value || '-',
    }),
    columnFactory.createNumberColumn('pListID', 'pListID', t('manager.modules.reservations.columns.pickList', {}, 'Plukkliste'), {
      width: 100,
      sortable: true,
      filterType: 'both',
      formatter: (value) => value != null ? String(value) : '-',
    }),
    columnFactory.createTextColumn('grShipmentNo', 'grShipmentNo', t('manager.modules.reservations.columns.grShipment', {}, 'GR Shipment'), {
      width: 110,
      sortable: true,
      visible: false,
      formatter: (value) => value != null ? String(value) : '-',
    }),
    // Allokering (TW 36362298): referansen til salgsordren varene er øremerket for.
    // EGNE kolonner — blandes ikke med Ordre (ERP)-kolonnen, som betyr «koblet til
    // ordre som finnes i Compass». Skjult som standard (kun relevant for type 3).
    columnFactory.createTextColumn('allocOrderNoERP', 'allocOrderNoERP', t('manager.modules.reservations.columns.allocOrderNoErp', {}, 'Allokert mot ordre (ERP)'), {
      width: 150,
      sortable: true,
      visible: false,
      filterType: 'both',
      formatter: (value) => value || '-',
    }),
    columnFactory.createTextColumn('allocLineNoERP', 'allocLineNoERP', t('manager.modules.reservations.columns.allocLineNoErp', {}, 'Allokert mot linje (ERP)'), {
      width: 130,
      sortable: true,
      visible: false,
      formatter: (value) => value != null ? String(value) : '-',
    }),
    columnFactory.createTextColumn('pOrderNo', 'pOrderNo', t('manager.modules.reservations.columns.pOrderNo', {}, 'Fra innkjøp'), {
      width: 110,
      sortable: true,
      visible: false,
      formatter: (value) => value != null ? String(value) : '-',
    }),
    columnFactory.createTextColumn('poLineNo', 'poLineNo', t('manager.modules.reservations.columns.poLineNo', {}, 'Fra innkjøpslinje'), {
      width: 120,
      sortable: true,
      visible: false,
      formatter: (value) => value != null ? String(value) : '-',
    }),
  ], [t, resTypeLabel]);

  // Summary calculations
  const summaryCalculations: SummaryCalculation<StcReservation>[] = useMemo(() => [
    {
      column: 'resQuantity' as keyof StcReservation,
      type: 'sum' as const,
      formatter: (value: number) => formatNumber(value),
    },
    {
      column: 'itemNo' as keyof StcReservation,
      type: 'count' as const,
      formatter: (value: number) => t('manager.modules.reservations.summaryCount', { '0': value }, `${value} reservasjoner`),
    },
  ], [t]);

  // Allokering (TW 36362298): manuell frigivelse av valgte allokeringsreservasjoner (type 3).
  // Andre typer kan ikke frigis her — backend guarder også (409).
  const handleReleaseAllocations = useCallback(async () => {
    const selected = reservations.filter(r => selectedRowIds.includes(r.resID));
    const allocations = selected.filter(r => r.resType === 3);
    if (allocations.length === 0) return;

    const confirmText = t(
      'manager.modules.reservations.releaseConfirm',
      { '0': allocations.length },
      `Frigi ${allocations.length} allokeringsreservasjon(er)? Varene blir tilgjengelige for vanlig plukk.`
    );
    if (!window.confirm(confirmText)) return;

    setLoading(true);
    setError(null);
    try {
      for (const alloc of allocations) {
        await reservationService.releaseAllocation(alloc.resID);
      }
      setSelectedRowIds([]);
      await loadData();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('manager.modules.reservations.releaseError', {}, 'Frigivelse feilet'));
      setLoading(false);
    }
  }, [reservations, selectedRowIds, loadData, t]);

  // Allokering (TW 36362298): re-print av allokeringsetikett for valgt(e) type 3-rad(er)
  // — mistet/ødelagt etikett. Konteksten gjenbygges fra reservasjonens PO-referanse
  // (pOrderNo/poLineNo); AllocationBarcode = ALC-{resID}. Ingen reportId sendes: backend
  // løser malen via DefaultReportId-regimet («Standard vareetikett-mal (innkjøpsordre)»,
  // ellers auto-pick) og gir tydelig feilmelding hvis ingen mal er konfigurert.
  const handlePrintAllocationLabels = useCallback(async () => {
    const allocations = reservations.filter(r => selectedRowIds.includes(r.resID) && r.resType === 3);
    if (allocations.length === 0) return;

    setError(null);

    for (const alloc of allocations) {
      const labelRequest = {
        numOfCopies: 1,
        itemNo: alloc.itemNo,
        quantity: alloc.resQuantity,
        locationName: alloc.binCode,
        pOrderNo: alloc.pOrderNo,
        pOrderLineNo: alloc.poLineNo,
        allocationResID: alloc.resID,
      };
      await tryPrintLabel(
        () => printingService.printItemLabelWithPO(alloc.companyNo, labelRequest),
        (printerId) => printingService.printItemLabelWithPO(alloc.companyNo, { ...labelRequest, printerIdOverride: printerId }),
        (result) => {
          if (!result.success) {
            setError(t('manager.modules.reservations.printLabelError', { '0': String(alloc.resID), '1': result.message || '' },
              `Utskrift av allokeringsetikett ${alloc.resID} feilet: ${result.message || 'ukjent feil'}`));
          }
        }
      );
    }
  }, [reservations, selectedRowIds, tryPrintLabel, t]);

  // Table actions
  const tableActions = useMemo(() => [
    {
      id: 'refresh',
      label: t('common.buttons.refresh', {}, 'Oppdater'),
      icon: RefreshCw,
      onClick: () => loadData(),
      variant: 'secondary' as const,
    },
    {
      id: 'print-allocation-label',
      label: t('manager.modules.reservations.printAllocationLabel', {}, 'Skriv ut etikett'),
      icon: Printer,
      onClick: () => handlePrintAllocationLabels(),
      variant: 'secondary' as const,
      // Aktiv kun når valgte rader finnes og ALLE er allokeringer (type 3)
      disabled: () => {
        const selected = reservations.filter(r => selectedRowIds.includes(r.resID));
        return selected.length === 0 || selected.some(r => r.resType !== 3);
      },
    },
    {
      id: 'release-allocation',
      label: t('manager.modules.reservations.releaseAllocation', {}, 'Frigi allokering'),
      icon: Unlock,
      onClick: () => handleReleaseAllocations(),
      variant: 'secondary' as const,
      // Aktiv kun når valgte rader finnes og ALLE er allokeringer (type 3)
      disabled: () => {
        const selected = reservations.filter(r => selectedRowIds.includes(r.resID));
        return selected.length === 0 || selected.some(r => r.resType !== 3);
      },
    },
  ], [loadData, t, handleReleaseAllocations, handlePrintAllocationLabels, reservations, selectedRowIds]);

  // Row ID extractor
  const getRowId = useCallback((row: StcReservation) => row.resID, []);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 pt-6 pb-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <BackButton onClick={onBack || (() => {})} />
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
                <ClipboardList size={32} className="text-teal-600" />
                {t('manager.modules.reservations.title', {}, 'Reservasjoner')}
              </h1>
              <p className="text-gray-600 mt-1">
                {t('manager.modules.reservations.subtitle', {}, 'Oversikt over aktive varereservasjoner i lageret')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* WarehouseTable */}
      <div className="flex-1 px-6 pb-6 min-h-0">
        <WarehouseTable<StcReservation>
          data={reservations}
          columns={columns}
          getRowId={getRowId}
          config={{
            persistenceKey: 'reservations-table',
            allowRowSelection: true,
            showPagination: true,
            showColumnConfig: true,
            showGlobalSearch: true,
            showFilters: true,
            showExport: true,
            showPrint: true,
            showSummary: true,
            defaultPageSize: 50,
            allowColumnReorder: true,
            allowColumnResize: true,
            stickyHeader: true,
            stickyFooter: true,
            compactMode: true,
          }}
          summaryCalculations={summaryCalculations}
          tableActions={tableActions}
          selectedRows={selectedRowIds}
          onSelectionChange={(ids) => setSelectedRowIds(ids)}
          loading={loading}
          error={error || undefined}
          aria-label={t('manager.modules.reservations.tableAria', {}, 'Reservasjoner tabell')}
        />
      </div>
      {/* Allokering: skrivervelger-fallback for re-print av allokeringsetikett */}
      {printerFallbackModal}
    </div>
  );
};
